import React, { useState, useMemo, useEffect } from "react";
import {
  useJobTitles,
  useCreateJobTitle,
  useDepartments,
} from "../../../api/queries/organizationQueries";
import { getApiErrorMessage } from "../../../utils/apiError";
import Layout from "../../../components/layout/Layout";
import Dialog from "../../../components/ui/Dialog";
import Pagination from "../../../components/ui/Pagination";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useCanModify } from "../../../hooks/usePermissions";

// Suggested categories; the API accepts any text, so stored values outside this list still show.
const JOB_CATEGORIES = [
  "Executive",
  "Management",
  "Supervisory",
  "Professional",
  "Staff",
];

const JobTitle = () => {
  // View-only roles see the list without create / edit / status actions
  const canModify = useCanModify("company-setup", { company: true });
  const localCompanyId = localStorage.getItem("companyId") || "";

  // Departments are loaded for the company being edited (defaults to the user's).
  const [deptCompanyId, setDeptCompanyId] = useState(localCompanyId);
  const { data: jobTitles = [] } = useJobTitles();
  const { data: departments = [], isLoading: isDeptLoading } = useDepartments(
    deptCompanyId,
    { enabled: !!deptCompanyId },
  );
  // The API saves job titles (new or edited) with POST /job-title.
  const saveJobTitle = useCreateJobTitle();
  const isSaving = saveJobTitle.isPending;

  /* ===================== LOCAL STATE ===================== */
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingJob, setPendingJob] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({
    id: "",
    title: "",
    departmentId: "",
    category: "Staff",
    isActive: true,
  });

  /* ===================== FILTER & PAGINATION STATE ===================== */
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  // Filter choices: the suggested list plus any other category already stored
  const categoryOptions = useMemo(
    () => [
      ...JOB_CATEGORIES,
      ...new Set(
        jobTitles
          .map((j) => j.category)
          .filter((c) => c && !JOB_CATEGORIES.includes(c)),
      ),
    ],
    [jobTitles],
  );
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: "title",
    direction: "asc",
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, statusFilter, sortConfig]);

  /* ===================== HELPER FUNCTIONS ===================== */
  const toCamelCase = (str) => {
    if (!str) return "Active";
    const s = str.toString().toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key)
      return <i className="bi bi-arrow-down-up ms-2 opacity-25 fs-060"></i>;
    return sortConfig.direction === "asc" ? (
      <i className="bi bi-sort-down ms-2 text-primary fs-070"></i>
    ) : (
      <i className="bi bi-sort-up ms-2 text-primary fs-070"></i>
    );
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  /* ===================== LOGIC HANDLERS ===================== */
  const handleOpenAdd = () => {
    setIsEditMode(false);
    setFormData({
      id: "",
      title: "",
      departmentId: "",
      category: "Staff",
      isActive: true,
    });
    setDeptCompanyId(localCompanyId);
    setShowModal(true);
  };

  const handleOpenEdit = (item) => {
    setIsEditMode(true);

    // FIX: Re-fetch departments for the specific company context if necessary
    const targetCompanyId = item.companyId || localCompanyId;
    if (targetCompanyId) setDeptCompanyId(targetCompanyId);

    setFormData({
      id: item.id,
      title: item.title,
      // FIX: Map the ID correctly whether flat or nested
      departmentId: item.departmentId || item.department?.id || "",
      category: item.category,
      isActive: item.isActive,
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();

    const payload = {
      title: formData.title,
      departmentId: formData.departmentId,
      category: formData.category,
      isActive: formData.isActive,
    };

    if (isEditMode) payload.id = formData.id;

    try {
      await saveJobTitle.mutateAsync(payload);
      toast.success(`Job title "${payload.title}" saved!`);
      setShowModal(false);
    } catch (err) {
      console.error("Save Error:", err);
      toast.error(getApiErrorMessage(err, "Failed to save"));
    }
  };

  const handleStatusToggle = (item) => {
    if (item.isActive) {
      setPendingJob(item);
      setShowConfirm(true);
    } else {
      executeStatusUpdate(item);
    }
  };

  const executeStatusUpdate = async (item) => {
    try {
      await saveJobTitle.mutateAsync({ ...item, isActive: !item.isActive });
      toast.success(`${item.title} updated!`);
      setShowConfirm(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Status update failed"));
    }
  };

  /* ===================== FILTER & PAGINATION LOGIC ===================== */
  const processedData = useMemo(() => {
    let items = Array.isArray(jobTitles) ? [...jobTitles] : [];

    items = items.filter((item) => {
      const deptObj = departments.find(
        (d) => d.id === (item.departmentId || item.department?.id),
      );
      const deptName = deptObj
        ? deptObj.departmentName
        : item.department?.departmentName || "";

      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        item.title.toLowerCase().includes(searchLower) ||
        deptName.toLowerCase().includes(searchLower);
      const matchesCategory =
        categoryFilter === "All" || item.category === categoryFilter;
      const matchesStatus =
        statusFilter === "All" ||
        (item.isActive ? "Active" : "Inactive") === statusFilter;

      return matchesSearch && matchesCategory && matchesStatus;
    });

    return items.sort((a, b) => {
      let valA, valB;
      const key = sortConfig.key;
      if (key === "department") {
        valA = (
          departments.find((d) => d.id === (a.departmentId || a.department?.id))
            ?.departmentName || ""
        ).toLowerCase();
        valB = (
          departments.find((d) => d.id === (b.departmentId || b.department?.id))
            ?.departmentName || ""
        ).toLowerCase();
      } else {
        valA = String(a[key] || "").toLowerCase();
        valB = String(b[key] || "").toLowerCase();
      }

      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [
    jobTitles,
    departments,
    searchTerm,
    categoryFilter,
    statusFilter,
    sortConfig,
  ]);

  const totalItems = processedData.length;
  const currentData = processedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <Link
              to="/admin"
              className="text-slate-500 fs-080 text-decoration-none"
            >
              <i className="bi bi-arrow-left"></i> Administration
            </Link>
            <h1 className="fw-bold text-dark m-0 page-title">Job Titles</h1>
            <p className="text-muted small mb-0">
              Define and manage company positions
            </p>
          </div>
          {canModify && (
            <button
              className="btn btn-sm px-3 text-white btn-navy h-35px d-flex align-items-center"
              onClick={handleOpenAdd}
            >
              <i className="bi bi-plus-lg me-2"></i>Add New
            </button>
          )}
        </div>

        <div className="row g-2 mb-4">
          <div className="col-md-6">
            <input
              type="text"
              className="form-control filter-input"
              placeholder="Search by title or department..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <select
              className="form-select filter-input"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="All">All Categories</option>
              {categoryOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-3">
            <select
              className="form-select filter-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="All">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="glass-table-card">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="custom-table-header sortable">
                <tr>
                  <th
                    className="ps-4 py-3 border-0"
                    onClick={() => requestSort("title")}
                  >
                    POSITION TITLE {getSortIcon("title")}
                  </th>
                  <th
                    className="py-3 border-0"
                    onClick={() => requestSort("department")}
                  >
                    DEPARTMENT {getSortIcon("department")}
                  </th>
                  <th className="py-3 border-0">CATEGORY</th>
                  <th className="py-3 border-0 text-center">STATUS</th>
                  <th className="pe-4 py-3 border-0 text-end">ACTIONS</th>
                </tr>
              </thead>
              <tbody className="border-top-0">
                {currentData.map((item) => {
                  const dept = departments.find(
                    (d) => d.id === (item.departmentId || item.department?.id),
                  );
                  return (
                    <tr key={item.id} className="align-middle">
                      <td className="ps-4 py-3">
                        <div className="fw-bold text-dark fs-085">
                          {item.title}
                        </div>
                      </td>
                      <td className="fs-080 text-slate-700">
                        {dept
                          ? dept.departmentName
                          : item.department?.departmentName || "N/A"}
                      </td>
                      <td>
                        <span className="badge border bg-light text-dark border-secondary-subtle fs-065 pad-5-10">
                          {item.category.toUpperCase()}
                        </span>
                      </td>
                      <td className="text-center">
                        <span
                          className={`status-pill status-pill--${item.isActive ? "green" : "gray"}`}
                        >
                          {toCamelCase(item.isActive ? "Active" : "Inactive")}
                        </span>
                      </td>
                      <td className="pe-4 text-end">
                        {canModify ? (
                          <>
                            <div className="d-flex justify-content-end gap-2">
                              <button
                                className="btn btn-sm btn-outline-primary px-3 fw-bold rounded-6 fs-075"
                                onClick={() => handleOpenEdit(item)}
                              >
                                Edit
                              </button>
                              <button
                                className={`btn btn-sm ${item.isActive ? "btn-outline-danger" : "btn-outline-success"} px-3 fw-bold rounded-6 fs-075`}
                                onClick={() => handleStatusToggle(item)}
                              >
                                {item.isActive ? "Deactivate" : "Activate"}
                              </button>
                            </div>
                          </>
                        ) : (
                          <span className="fs-070 text-slate-400">
                            View only
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Pagination
            page={currentPage}
            limit={itemsPerPage}
            total={totalItems}
            onPageChange={setCurrentPage}
            onLimitChange={(v) => {
              setItemsPerPage(v);
              setCurrentPage(1);
            }}
            pageSizes={[10, 25, 50]}
          />
        </div>
      </div>

      {showConfirm && (
        <Dialog
          title="Confirm Deactivation"
          onClose={() => setShowConfirm(false)}
          size="sm"
        >
          <div className="text-start">
            <p className="text-muted small">
              Are you sure you want to deactivate{" "}
              <strong>{pendingJob?.title}</strong>?
            </p>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button
                className="btn btn-outline-secondary profile-btn"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger profile-btn"
                onClick={() => executeStatusUpdate(pendingJob)}
              >
                Yes, Deactivate
              </button>
            </div>
          </div>
        </Dialog>
      )}

      {showModal && (
        <Dialog
          title={<>{isEditMode ? "Edit" : "Add"} Job Title</>}
          onClose={() => setShowModal(false)}
          size="md"
        >
          <form onSubmit={handleSave}>
            <div className="row g-3">
              <div className="col-12">
                <label className="profile-label">Position Title</label>
                <input
                  type="text"
                  className="form-control"
                  required
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                />
              </div>
              <div className="col-12">
                <label className="profile-label">Department</label>
                <select
                  className="form-select"
                  required
                  value={formData.departmentId}
                  onChange={(e) =>
                    setFormData({ ...formData, departmentId: e.target.value })
                  }
                >
                  <option value="">
                    {isDeptLoading ? "Loading..." : "Select Department"}
                  </option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.departmentName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12">
                <label className="profile-label">Category</label>
                <select
                  className="form-select"
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({ ...formData, category: e.target.value })
                  }
                >
                  {/* Category is free text in the API: keep an older stored value selectable */}
                  {formData.category &&
                    !JOB_CATEGORIES.includes(formData.category) && (
                      <option value={formData.category}>
                        {formData.category}
                      </option>
                    )}
                  {JOB_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-12">
                <div className="form-check form-switch mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                  />
                  <label className="form-check-label fs-085">
                    Active Status
                  </label>
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
              <button
                type="button"
                className="btn btn-outline-secondary profile-btn"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary profile-btn"
                disabled={isSaving}
              >
                {isSaving ? "Processing..." : "Save Job Title"}
              </button>
            </div>
          </form>
        </Dialog>
      )}
    </Layout>
  );
};

export default JobTitle;
